import { type MeResponse, type PermissionCode, isAdminRole } from "@seagames/contracts";
import { groupGrants } from "../travel/access";

/**
 * Who sees what on the Sport entries pages (M7b, EOI → EBN). NONE of this
 * decides anything: the API scopes every read to the caller's organisations
 * and re-checks every write (window, final lock, `can_write` scope and
 * `competition.declare`). It only picks which page to draw.
 *
 * Who someone IS comes from the active role (bug 7): an ORGANIZATION login is
 * a NOC and gets its own editor; admins get the overview of every NOC; an
 * OPERATION group with the permission gets the list of the NOCs its scope
 * covers, without the admin-only matrix and export.
 */

type Session = Pick<MeResponse, "active_role" | "group">;

/** The permission that opens the pages for a group (nav + route guard). */
export const ENTRIES_PAGE_PERMISSIONS = ["competition.declare"] as const satisfies readonly PermissionCode[];

export type EntriesView = "admin" | "host" | "noc";

export function entriesView(me: Session): EntriesView | null {
	if (isAdminRole(me.active_role)) return "admin";
	if (!groupGrants(me, "competition.declare")) return null;
	if (me.active_role === "ORGANIZATION") return "noc";
	if (me.active_role === "OPERATION") return "host";
	return null;
}

/** The route guard and the nav: anyone with a view. */
export const canSeeEntries = (me: Session): boolean => entriesView(me) !== null;

/** Reopen a submitted phase: SUPER_ADMIN only (the API answers 403 to anyone else). */
export const canReopenEntries = (me: Session): boolean => me.active_role === "SUPER_ADMIN";
